import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_BLOCK_BORDER_COLOR } from '../../../../shared/documentColorDefaults';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import {
  EditorDefaultSidePanelType,
  setActiveSidePanel,
  setIsAdvancedSpacingModelOpen,
  setIsReplaceModeActive,
} from '../../../../grid/reduxStore/blockStyleSettingsSlice';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import IcoLock from '../../../../../icon/icons/IcoLock';
import IcoLockOpen from '../../../../../icon/icons/IcoLockOpen';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice.ts';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice.ts';

import {
  DropDownSection,
  NumberInputBoxSingleCol,
  NumInputBoxTwoCol,
  SecondaryMenu,
  SecondaryMenuSectionTitle,
  SecondaryMenuTrigger,
  TextInputBoxSingleCol,
} from '../../../component';

import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { useNewBlockStyleSettings } from '../../../../hooks/useNewBlockSettings';
import { getBlockSidesFromString } from 'components/editor/GridDndEditor/Block/Helper/BlockSides.tsx';
import { useBlockContentWithSectionId } from 'components/editor/Sections/useBlockContent.tsx';
import { ImagesLayout } from 'components/library/images/ImagesLayout.tsx';
import { ColorPickerWithUndoRedoSection } from '../../../component/ColorPickerWithundoRedoSection.tsx';
import { DIMENSION_HEIGHT, DIMENSION_WIDTH } from 'components/editor/SidePanel/content/DesignSettings/common/useAspectRatio.ts';
import { useNewAspectRatio } from 'components/editor/SidePanel/content/DesignSettings/common/useNewAspectRatio.ts';

export function ImageBlockWithUndoRedoSettings() {
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));
  const blockContent = useBlockContentWithSectionId(sectionId ?? '', blockId);

  if (!blockContent) {
    return null;
  }

  return <ImageBlockSettingsContent key={blockId} blockContent={blockContent} blockId={blockId} sectionId={sectionId as string} />;
}

const ImageBlockSettingsContent = ({
  blockContent,
  blockId,
  sectionId,
}: {
  blockContent: BlockContent;
  blockId: string;
  sectionId: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const newBlockStyleSettings = useNewBlockStyleSettings(blockId, sectionId);

  const {
    getBlockStyle,
    updateBlockStyleInUi,
    changeBlockBorderSide,
    updateBlockStyleWithUndo,
    saveCurrentStateToRef,
    changeBorderWidthInUi,
    changeBorderWidth,
    changeBorderRadiusInUi,
    changeBorderRadius,
  } = newBlockStyleSettings;

  const {
    opacity = 0,
    imageAlt = '',
    imageLink = '',
    imageAspectRatioLock = false,
    borderColor = DEFAULT_BLOCK_BORDER_COLOR,
    currentBorderSide = [],
    borderRadius = 0,
    borderWidth = 0,
  } = useMemo(() => getBlockStyle(blockId), [getBlockStyle, blockId]);

  const width = blockContent?.blockConfig.width || 0;
  const height = blockContent?.blockConfig.height || 0;

  const { handleImageDimensionChange } = useNewAspectRatio({
    imageAspectRatioLock,
    sectionId: sectionId ?? '',
    blockId,
  });

  const handleFocus = useCallback(() => {
    saveCurrentStateToRef(getBlockStyle(blockId));
  }, [getBlockStyle, saveCurrentStateToRef]);

  const [typedWidth, setTypedWidth] = useState<number | null>(null);
  const [typedHeight, setTypedHeight] = useState<number | null>(null);

  const originalDimensionsRef = useRef<{
    width: number;
    height: number;
    blockId: string;
    blockContent: BlockContent;
  } | null>(null);

  useEffect(() => {
    setTypedWidth(null);
    setTypedHeight(null);
    originalDimensionsRef.current = null;
  }, [blockId]);

  const handleDimensionFocus = useCallback(() => {
    originalDimensionsRef.current = {
      width,
      height,
      blockId,
      blockContent: {
        ...blockContent,
        blockConfig: { ...blockContent.blockConfig },
      },
    };
  }, [width, height, blockId, blockContent]);

  const handleWidthChange = (newWidth: number) => {
    setTypedWidth(newWidth);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, newWidth, false, originalDimensionsRef.current);
    }
  };

  const handleWidthBlur = () => {
    if (typedWidth !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, typedWidth, true, originalDimensionsRef.current);
    }

    setTypedWidth(null);
  };

  const handlePressingReturn = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLElement).blur();
    }
  };

  const handleHeightChange = (newHeight: number) => {
    setTypedHeight(newHeight);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, newHeight, false, originalDimensionsRef.current);
    }
  };

  const handleHeightBlur = () => {
    if (typedHeight !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, typedHeight, true, originalDimensionsRef.current);
    }

    setTypedHeight(null);
  };

  const handleBackButtonClick = useCallback(() => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.UPLOADS_LIBRARY_PANEL }));
  }, [dispatch]);

  const handleBorderSideChange = useCallback(
    (borderSide: string[]) => {
      const sides = getBlockSidesFromString(borderSide);
      if (JSON.stringify(sides) !== JSON.stringify(currentBorderSide)) {
        changeBlockBorderSide(sides);
      }
    },
    [currentBorderSide, changeBlockBorderSide]
  );

  return (
    <SecondaryMenu
      testId={'image-block-settings-component'}
      menuTitle={t('document.grid.image.design.title')}
      backButtonOnClick={handleBackButtonClick}
    >
      <Stack direction="row" gap={2} alignItems="center">
        {blockContent?.content && (
          <ImagesLayout.GalleryTile size="small">
            <ImagesLayout.GalleryTileImage size="small" url={blockContent?.content} title={imageAlt} />
          </ImagesLayout.GalleryTile>
        )}
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.UPLOADS_LIBRARY_PANEL }));
            dispatch(setIsReplaceModeActive(true));
          }}
        >
          Replace
        </Button>
      </Stack>

      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.image.design.display')}</SecondaryMenuSectionTitle>
        <NumInputBoxTwoCol
          title={t('document.grid.image.design.opacity')}
          ariaLabel="%"
          inputValue={opacity}
          onChange={(opacity) => {
            updateBlockStyleInUi({ opacity });
          }}
          onBlur={(opacity: number) => {
            updateBlockStyleWithUndo({ opacity });
          }}
          onFocus={handleFocus}
          limitedNum={100}
          disableNegativeNum
          testId="image-opacity-input"
        />
        <Stack gap={1} direction="row" alignItems="end">
          <Stack direction="row" gap={2}>
            <NumberInputBoxSingleCol
              title={t('document.grid.image.design.width')}
              onChange={handleWidthChange}
              onBlur={handleWidthBlur}
              onFocus={handleDimensionFocus}
              value={width}
              endAdornmentText="px"
              testId="image-width-input"
            />
            <NumberInputBoxSingleCol
              title={t('document.grid.image.design.height')}
              onChange={handleHeightChange}
              onBlur={handleHeightBlur}
              onFocus={handleDimensionFocus}
              value={height}
              endAdornmentText="px"
              testId="image-height-input"
            />
          </Stack>

          <IconButton
            data-testid="aspect-ratio-lock-button"
            onClick={() => updateBlockStyleWithUndo({ imageAspectRatioLock: !imageAspectRatioLock })}
          >
            {imageAspectRatioLock ? <IcoLock /> : <IcoLockOpen />}
          </IconButton>
        </Stack>

        <TextInputBoxSingleCol
          title={t('document.grid.image.design.image_alt_text')}
          descriptionText={t('document.grid.image.design.image_alt_text_dec')}
          onChange={(imageAlt) => updateBlockStyleInUi({ imageAlt })}
          onBlur={(imageAlt: string) => updateBlockStyleWithUndo({ imageAlt })}
          onFocus={handleFocus}
          value={imageAlt}
          onKeyDown={handlePressingReturn}
          testId="image-alt-input"
        />

        <TextInputBoxSingleCol
          title={t('document.grid.image.design.link')}
          descriptionText={t('document.grid.image.design.link_dec')}
          onChange={(imageLink) => updateBlockStyleInUi({ imageLink })}
          onBlur={(imageLink: string) => updateBlockStyleWithUndo({ imageLink })}
          value={imageLink}
          onFocus={handleFocus}
          startAdornmentText={'https://'}
          onKeyDown={handlePressingReturn}
          testId="image-link-input"
        />
      </Stack>
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.image.design.border')}</SecondaryMenuSectionTitle>
        <ColorPickerWithUndoRedoSection
          currentColor={borderColor}
          onOpen={handleFocus}
          onColorChange={(borderColor) => updateBlockStyleInUi({ borderColor })}
          onClose={(borderColor: string) => updateBlockStyleWithUndo({ borderColor })}
        />
        <DropDownSection currentSelection={currentBorderSide} handleSelectionChange={handleBorderSideChange} testId={'border-dropdown'} />
        <NumInputBoxTwoCol
          inputValue={borderWidth}
          onChange={(borderWidth: number) => changeBorderWidthInUi(borderWidth)}
          onBlur={(borderWidth: number) => changeBorderWidth(borderWidth)}
          onFocus={handleFocus}
          title={t('document.grid.text.design.border_width')}
          testId={'border-width'}
          disableNegativeNum
        />
        <NumInputBoxTwoCol
          title={t('document.grid.text.design.border_radius')}
          inputValue={borderRadius}
          disableNegativeNum
          onFocus={handleFocus}
          onChange={(borderRadius) => changeBorderRadiusInUi({ radius: borderRadius })}
          onBlur={(borderRadius) => changeBorderRadius({ radius: borderRadius })}
          testId={'border-radius'}
        />

        <SecondaryMenuTrigger
          ButtonText={t('document.grid.text.advanced_spacing.title')}
          ButtonDesc={t('document.grid.text.advanced_spacing.adjust_left_top_right_bottom_block_padding')}
          handleClick={() => {
            dispatch(setIsAdvancedSpacingModelOpen(true));
          }}
        />
      </Stack>
    </SecondaryMenu>
  );
};
